import React from 'react'
import Select, { StylesConfig, GroupBase, OptionsOrGroups } from 'react-select'

interface SelectOption {
  value: string
  label: string
}

interface CustomSelectProps {
  options: OptionsOrGroups<SelectOption, GroupBase<SelectOption>>
  value?: SelectOption | null
  onChange: (selected: SelectOption | null) => void
  placeholder?: string
  isSearchable?: boolean
  isDisabled?: boolean
  minHeight?: number
  className?: string
  onMenuOpen?: () => void
  backgroundColor?: string // 배경색
  boxShadow?: string // 그림자
  /** 미지정 시 플레이스홀더 기본색(rgba 40%) 사용 */
  placeholderColor?: string
  /** true면 드롭다운 메뉴를 document.body로 포털 렌더.
   *  overflow-hidden 컨테이너(바텀시트 등) 안에서 메뉴가 잘리는 것을 방지. */
  menuPortal?: boolean
  /** 메뉴 펼침 방향. 미지정 시 포털이면 'auto', 아니면 'bottom'. */
  menuPlacement?: 'auto' | 'top' | 'bottom'
  /** 드롭다운 목록 최대 높이(px). 하단 시트에서 완료 버튼을 안 덮도록 가용 높이를 넘겨줄 때 사용. */
  maxMenuHeight?: number
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = '선택해주세요',
  isSearchable = false,
  isDisabled = false,
  minHeight = 44,
  className = '',
  onMenuOpen,
  backgroundColor = '#FFFFFF',
  boxShadow = '0px 4px 10px 0px rgba(117, 76, 0, 0.1)',
  placeholderColor,
  menuPortal = false,
  menuPlacement,
  maxMenuHeight = 220,
}) => {
  const placeholderTextColor =
    placeholderColor ?? 'rgba(75, 54, 33, 0.4)'

  const customStyles: StylesConfig<SelectOption, false> = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: backgroundColor,
      borderColor: state.isFocused ? '#472410' : 'rgba(71, 36, 16, 0.2)',
      borderWidth: '1px',
      borderRadius: '8px',
      minHeight: `${minHeight}px`,
      padding: '0 4px',
      boxShadow: boxShadow,
      opacity: state.isDisabled ? 1 : (provided.opacity ?? 1),
      '&:hover': {
        borderColor: state.isDisabled
          ? 'rgba(71, 36, 16, 0.2)'
          : '#472410',
      },
      cursor: state.isDisabled ? 'default' : 'pointer',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: placeholderTextColor,
      fontWeight: 'regular',
      fontSize: '16px',
      fontFamily: 'Pretendard, sans-serif',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'rgba(75, 54, 33, 0.8)', // #4B3621, 80%
      fontWeight: 'regular',
      fontSize: '16px',
      fontFamily: 'Pretendard, sans-serif',
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '8px',
      border: '1px solid #472410', // 진한 브랜드 브라운 — 포커스된 컨트롤 테두리와 통일(기존 초록 #749D2F 대체)
      backgroundColor: '#FFFFFF',
      boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.1)',
      marginTop: '4px',
      // overflow: 'hidden' 제거 — 이게 목록 스크롤을 막고 있었음.
      // 모서리 둥글게는 menuList의 overflow로 처리.
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '0',
      // 포털(바텀시트) 사용 시엔 maxHeight를 하드코딩하지 않고 react-select가
      // menuPlacement="auto"로 계산한 가용 화면 높이(provided.maxHeight)를 그대로 사용
      // → 메뉴가 뷰포트 밖으로 넘쳐 아래가 잘리는 일이 없음.
      ...(menuPortal ? {} : { maxHeight: '180px' }),
      overflowY: 'auto', // 세로 스크롤 활성화
      borderRadius: '8px',
      // iOS WKWebView / Galaxy WebView 터치 관성 스크롤
      WebkitOverflowScrolling: 'touch',
      // 목록 끝까지 스크롤해도 뒤 피드로 스크롤이 전파되지 않게 막음
      overscrollBehavior: 'contain',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor:
        state.isSelected || state.isFocused ? '#F8F4EE' : '#FFFFFF',
      color: 'rgba(75, 54, 33, 0.8)', // #4B3621, 80%
      fontSize: '16px',
      fontFamily: 'Pretendard, sans-serif',
      fontWeight: 'regular',
      padding: '10px 12px',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#FAF6EF',
      },
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: '#6B5E46',
      padding: '8px',
      '&:hover': {
        color: '#6B5E46',
      },
    }),
    input: (provided) => ({
      ...provided,
      color: '#6B5E46',
      fontSize: '14px',
      fontFamily: 'Pretendard, sans-serif',
    }),
    // 포털 렌더 시: 오버레이(z-40)·시트(z-50) 위로 떠야 함
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  }

  return (
    <div className={className || ''}>
      <Select<SelectOption>
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        isSearchable={isSearchable}
        isDisabled={isDisabled}
        styles={customStyles}
        classNamePrefix="custom-select"
        onMenuOpen={onMenuOpen}
        maxMenuHeight={maxMenuHeight}
        menuShouldScrollIntoView={false}
        menuPortalTarget={menuPortal ? document.body : undefined}
        menuPosition={menuPortal ? 'fixed' : 'absolute'}
        menuPlacement={menuPlacement ?? (menuPortal ? 'auto' : 'bottom')}
      />
    </div>
  )
}

export default CustomSelect
export type { SelectOption }
